import React, {useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {Menu, MenuItem, Sidebar, sidebarClasses, SubMenu,} from "react-pro-sidebar";
import {
    FaBook,
    FaBookOpen,
    FaCheckCircle,
    FaExpand,
    FaFilePdf,
    FaPlayCircle,
    FaPoll,
    FaQuestionCircle,
} from "react-icons/fa";
import {buildStyles, CircularProgressbar} from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import {FaMinimize} from "react-icons/fa6";

/**
 * Helper – return the appropriate icon component for a content type.
 */
function getIconFor(type) {
    switch (type) {
        case "video":
            return <FaPlayCircle/>;
        case "document":
            return <FaFilePdf/>;
        case "exam":
            return <FaQuestionCircle/>;
        case "survey":
            return <FaPoll/>;
        default:
            return null;
    }
}

/**
 * CourseSidebar – fully aligned with `react-pro-sidebar` v1 API
 *
 * Props
 * ├── course                – { name: string }
 * ├── progress              – number (0-100)
 * ├── courseContent         – array<{ id, type, title? }>
 * ├── completedContentIds   – array of completed item ids
 * ├── onItemClick           – (index:number) => void
 * └── currentContentIndex   – number (highlight current lesson)
 */
export default function CourseSidebar({
                                          course,
                                          progress,
                                          courseContent,
                                          completedContents,
                                          onItemClick = idx => {
                                          },
                                          currentContentIndex = 0,
                                      }) {

    const [isCollapsed, setIsCollapsed] = useState(false);
    const [sidebarWidth, setSidebarWidth] = useState("350px");

    useEffect(() => {
        const updateSidebarWidth = () => {
            if (window.innerWidth <= 768) {
                setSidebarWidth("auto");
            } else {
                setSidebarWidth("25%");
            }
        };

        // Set initial width
        updateSidebarWidth();

        // Add event listener for window resize
        window.addEventListener("resize", updateSidebarWidth);

        return () => {
            window.removeEventListener("resize", updateSidebarWidth);
        };
    }, []);

    // TODO fetch course content from API instead of sorting it here
    const sortedItems = useMemo(() => {
        return [...courseContent].sort((a, b) => a.position - b.position);
    }, [courseContent]);

    return (
        <Sidebar
            collapsed={isCollapsed}
            collapsedWidth={"120px"}
            width={sidebarWidth}
            rootStyles={{
                [`.${sidebarClasses.container}`]: {
                    background: "linear-gradient(to bottom, #5f7afb, #ffffff)",
                    /*height: "100vh",*/
                },
                [`.${sidebarClasses.root}`]: {
                    background: "linear-gradient(to bottom, #5f7afb, #ffffff)",
                    color: "#ffffff",
                    fontSize: "16px",
                    fontWeight: "bold",
                    padding: "0px",
                    borderRadius: "0px",
                },
            }}
        >
            <Menu
                closeOnClick={false}
                menuItemStyles={{
                    button: ({level, active}) => {
                        if (level === 0)
                            return {
                                color: "#ffffff",
                                background: active ? "linear-gradient(to bottom, #1d34a3, #5f7afb)" : undefined,
                                "&:hover": {
                                    backgroundColor: "#273b8e",
                                },
                            };
                        else
                            return {
                                color: "#ffffff",
                                background: active ? "linear-gradient(to bottom, #1d34a3, #7a8ded)" : undefined,
                                "&:hover": {
                                    background: "linear-gradient(to bottom, #1d34a3, #7a8ded)",
                                },
                            };
                    },
                    icon: ({active}) => {
                        return {
                            color: active ? "#ffffff" : "#000000",
                        };
                    },
                    label: ({active}) => {
                        return {
                            color: active ? "#ffffff" : "#000000",
                        };
                    },
                    root: ({active}) => {
                        return {
                            display: "block",
                        };
                    },
                }}
            >
                {/* Toggle Button */}
                <MenuItem
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    icon={isCollapsed ? <FaExpand/> : <FaMinimize/>}
                    style={{
                        backgroundColor: "#5f7afb",
                        color: "#fff",
                        border: "none",
                        padding: "10px",
                        cursor: "pointer",
                        width: "100%",
                        textAlign: "center",
                    }}
                >
                    {isCollapsed ? "Genişlet" : "Daralt"}
                </MenuItem>
                {/* Header */}
                <MenuItem disabled
                          icon={<FaBook/>}
                          rootStyles={{
                              justifyItems: "center",
                              /*[`a`]: {
                                  display: 'block !important',
                                  textAlign: 'center',
                                  alignItems: 'center',
                                  alignContent: 'center',
                              },*/
                          }}>
                    <span className="font-semibold text-base truncate text-center w-full">
                        {course?.name ?? "Kurs"}
                    </span>
                </MenuItem>

                {/* Progress ring */}
                <MenuItem disabled
                          rootStyles={{
                              display: "block", justifyItems: "center", alignItems: "center", padding: "16px 0",
                              [`a, span, div`]: {
                                  display: 'block !important',
                                  textAlign: 'center',
                                  alignItems: 'center',
                                  alignContent: 'center',
                                  height: "100% !important",
                                  textWrap: "wrap"
                              },
                          }}>
                    <div className="block justify-items-center">
                        <span className="text-sm font-medium text-white">Tamamlama Oranı</span>
                        <div style={{width: "50%", height: "50%", margin: "0 auto"}}>
                            <CircularProgressbar
                                value={progress}
                                text={`${progress}%`}
                                styles={buildStyles({
                                    textSize: "16px",
                                    pathColor: "#1d34a3",
                                    textColor: "#ffffff",
                                    trailColor: "#7a8ded",
                                })}
                            />
                        </div>
                    </div>
                </MenuItem>

                {/* Course content list */}
                <SubMenu label="Dersler" defaultOpen
                         icon={<FaBookOpen/>} className="text-white">
                    {sortedItems.map((item, idx) => (
                        <MenuItem
                            key={item.id ?? idx}
                            icon={getIconFor(item.type)}
                            active={idx === currentContentIndex - 1}
                            onClick={() => onItemClick(courseContent[idx])}
                            suffix={
                                completedContents.includes(item.id) ? (
                                    <span className="text-xs text-green-500">
                                        <FaCheckCircle color={
                                            completedContents.includes(item.id) ? "#4caf50" : "#000000"
                                        }
                                                       size={16}/>
                                    </span>
                                ) : null
                            }
                        >
                            {item.title || `İçerik ${idx + 1}`}
                        </MenuItem>
                    ))}
                </SubMenu>
            </Menu>
        </Sidebar>
    );
}

CourseSidebar.propTypes = {
    course: PropTypes.shape({name: PropTypes.string}),
    progress: PropTypes.number,
    courseContent: PropTypes.any,
    completedContentIds: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ),
    onItemClick: PropTypes.func,
    currentContentIndex: PropTypes.number,
};